<template>
    <v-col v-bind="DASHBOARD_WIDGET_COLUMNS.SMALL">
        <v-card>
            <v-toolbar color="grey-lighten-4" density="compact" flat>
                <template #title>{{ widget.body?.title }}</template>
                <template #append>
                    <v-tooltip :text="t('common.dashboard.tooltips.export')" location="top">
                        <template #activator="{ props: tooltipProps }">
                            <v-btn
                                v-bind="tooltipProps"
                                color="green"
                                icon="mdi-file-excel"
                                size="small"
                                variant="text"
                                @click="generateReport"
                            />
                        </template>
                    </v-tooltip>
                </template>
            </v-toolbar>
            <v-divider />
            <v-card-text>
                <OrdersProductsDelayedListDialog
                    :is-open="dialogOpen"
                    :orders-products-delayed="ordersProducts"
                    :partner="partner"
                    @close-delayed-orders-products-dialog="closeDialog"
                />

                <v-data-table
                    :headers="headers"
                    :items="sortedOrders"
                    density="compact"
                    hide-default-footer
                    item-value="orderLocation"
                >
                    <template #item.details="{ item }">
                        <v-tooltip v-if="item.orderLocation" :text="t('common.dashboard.tooltips.showDetails')" location="top">
                            <template #activator="{ props: tooltipProps }">
                                <v-btn
                                    v-bind="tooltipProps"
                                    color="blue"
                                    icon="mdi-ballot"
                                    size="small"
                                    variant="text"
                                    @click="showDetails(item)"
                                />
                            </template>
                        </v-tooltip>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    </v-col>
</template>

<script setup lang="ts">
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { errorHandler } from '@/composables/errorHandler'
import { useDashboardStore, useUserStore } from '@/stores'
import type { DashboardPermission, DashboardWidget } from '@/stores/dashboard'
import type { DataTableHeader } from '@/types/dataTable'
import { DASHBOARD_WIDGET_COLUMNS, getOrdersLocationSortRank } from '@/views/Dashboard/dashboardUtils'

import OrdersProductsDelayedListDialog from './OrdersProductsDelayedListDialog.vue'

type DelayedOrderRow = { orderLocation?: string | null; toSend?: unknown }

type DelayedDialogProductRow = {
    prodName?: string
    toSend?: unknown
    quantity?: unknown
    partner?: string
    productId?: string | number
}

// 2. Props / Emits
const props = withDefaults(
    defineProps<{
        widget: DashboardWidget
        permissions?: DashboardPermission[]
    }>(),
    { permissions: () => [] }
)

// 3. Constants
const headers: DataTableHeader[] = [
    { title: 'Nazwa', key: 'orderLocation', sortable: false },
    { title: 'Ilość', key: 'toSend', sortable: false },
    { title: 'Szczegóły', key: 'details', sortable: false, align: 'end' }
]

// 4. Stores & top-level services
const dashboardStore = useDashboardStore()
const userStore = useUserStore()
const { t } = useI18n()

// 5. Reactive state (refs/reactive)
const dialogOpen = ref(false)
const ordersProducts = ref<DelayedDialogProductRow[]>([])
const partner = ref('')

// 7. Computed
const sortedOrders = computed(() => {
    const c = props.widget?.body?.content
    const rows: DelayedOrderRow[] = Array.isArray(c) ? (c as DelayedOrderRow[]) : []
    return [...rows].sort((a, b) => getOrdersLocationSortRank(a?.orderLocation) - getOrdersLocationSortRank(b?.orderLocation))
})

// 8. Handlers / methods
async function showDetails(item: DelayedOrderRow): Promise<void> {
    partner.value = item.orderLocation || ''

    try {
        const response = await dashboardStore.getOrdersProductsDelayed({
            query: `partner=${partner.value}`
        })
        ordersProducts.value = (response?.data?.results || []) as DelayedDialogProductRow[]
        dialogOpen.value = true
    } catch (error) {
        errorHandler(error)
    }
}

async function generateReport(): Promise<void> {
    try {
        await dashboardStore.generateOrdersDelayedReport({ userEmail: userStore.userEmail || '' })
    } catch (error) {
        errorHandler(error)
    }
}

function closeDialog(): void {
    dialogOpen.value = false
    ordersProducts.value = []
    partner.value = ''
}
</script>
